import { array } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import type { NewReportSlideFragmentLayout } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';

import joinClasses from 'webapp/helpers/join-classes';
import {
	getPositionPropertyFromOrderProperty,
	type OrderProperty,
} from 'webapp/components/reports/editor-base';
import ReportsFragmentGridAddFragmentButtonComponent from 'webapp/components/reports/fragment-grid/add-fragment-button';

import ReportSlideFragment from 'webapp/models-custom/report-slide-fragment';

import type { PagelessTourSettings } from 'webapp/services/flow/tour/reports-pageless';

interface ReportsFragmentGridAddColumnComponentInterface {
	Args: {
		fragment: ReportSlideFragment;
		orderProperty: OrderProperty;
		addColumn: (
			fragmentType: NewReportSlideFragmentLayout,
			rowIndex: number,
			columnIndex: number,
		) => void;
		insertBefore?: boolean;
		isVisible?: boolean;
		backgroundColor?: string | null;
	};
}

export default class ReportsFragmentGridAddColumnComponent extends Component<ReportsFragmentGridAddColumnComponentInterface> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('flow/tour/reports-pageless')
	declare flowTourReportsPagelessService: Service.Flow.Tour.ReportsPageless;

	availableTourSettings: PagelessTourSettings = {
		id: TourId.REPORTS__PAGELESS_ADD_COLUMN,
		checkStartConditions: this.checkTourStartConditions.bind(this),
	};

	constructor(owner: Owner, args: ReportsFragmentGridAddColumnComponentInterface['Args']) {
		super(owner, args);

		schedule('afterRender', () => {
			this.setupTourBindings();
		});
	}

	get positionProperty() {
		return getPositionPropertyFromOrderProperty(this.args.orderProperty);
	}

	get isAddColumnShown() {
		return this.args.fragment.isAddColumnShown;
	}

	@action
	addFragment(fragmentType: NewReportSlideFragmentLayout) {
		if (!this.args.isVisible) {
			return;
		}

		if (!this.isAddColumnShown) {
			return;
		}

		const pos = this.args.fragment[this.positionProperty];

		const row = pos.row;
		let column = pos.column;

		if (!this.args.insertBefore) {
			column++;
		}

		this.args.addColumn(fragmentType, row, column);
	}

	private setupTourBindings() {
		const tourSettings = this.availableTourSettings;
		this.flowTourReportsPagelessService.bindTourCheck(tourSettings.id, this, () => {
			if (tourSettings.checkStartConditions()) {
				this.flowTourReportsPagelessService.startTour(tourSettings.id);
			}
		});
	}

	private checkTourStartConditions(): boolean {
		if (!this.args.fragment.isOnPagelessReport || !this.args.isVisible) {
			return false;
		}

		// this also checks if the fragment is a title or board
		if (!this.isAddColumnShown) {
			return false;
		}

		return true;
	}

	override willDestroy() {
		super.willDestroy();
		this.flowTourReportsPagelessService.unbindTourCheck(this.availableTourSettings.id, this);
	}

	<template>
		<div
			class={{joinClasses
				(array
					"add-fragment"
					"add-fragment--column"
					(unless @isVisible "hidden")
					(if @insertBefore "add-fragment--left" "add-fragment--right")
					(unless this.isAddColumnShown "add-fragment--hide-add")
				)
			}}
		>
			<div class="add-fragment-wrapper">
				<div class="add-fragment__actions">
					<ReportsFragmentGridAddFragmentButtonComponent
						@isVisible={{@isVisible}}
						@onAddFragment={{this.addFragment}}
						@backgroundColor={{@backgroundColor}}
						@hideTitle={{true}}
					/>
				</div>
			</div>
		</div>

		{{!-- {{#if @insertBefore}}
			<Common::UiIcon @name="GripVertical" @size="sm" @class="handle-vertical" />
		{{/if}} --}}
	</template>
}
